/**
 * Which matters this enquiry would become.
 *
 * ===========================================================================
 * WHY THIS SITS ON THE LEAD AND NOT ON THE CONVERSION SCREEN
 * ===========================================================================
 * It is the decision that lets conversion be automatic later.
 *
 * A signature tells you THAT somebody engaged. It does not tell you what to open, and
 * one signed engagement might mean one matter or three. If that is decided at the
 * moment of conversion, somebody has to be present and thinking when the signature
 * arrives — which is exactly the thing an automatic flow cannot rely on.
 *
 * Decided here instead, while a fee earner is already forming a judgement about whether
 * the enquiry is real, the plan exists before the signature does.
 *
 * Several, not one: an enquiry may span two matters (CR-08), and conversion opens one
 * case per service.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type Lead,
} from '@legalcrm/api';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  useToast,
} from '@legalcrm/ui';
import { useState } from 'react';

interface Service {
  id: string;
  name: string;
  key?: string;
}

export function LeadServicesCard({ lead }: { lead: Lead }) {
  const api = useApi();
  const { toast } = useToast();

  // Active only, because this is a chooser. A matter type the firm has retired must not
  // be tickable on a new enquiry; one already recorded against this lead still shows,
  // because `chosen` comes from the lead itself rather than from this list.
  const catalogue = useApiQuery<{ results: Service[] } | Service[]>(
    qk.list('services', { is_active: 'true' }),
    STAFF.services,
    { query: { is_active: 'true' } },
  );
  const available = Array.isArray(catalogue.data)
    ? catalogue.data
    : (catalogue.data?.results ?? []);

  const chosen = (lead.services ?? []).map((s) => s.id);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>(chosen);

  const save = useApiMutation<Lead, string[]>(
    {
      // The whole list, every time. Sending only additions would leave the screen and
      // the server disagreeing the first time somebody unticks one.
      mutationFn: (ids) =>
        api.post<Lead>(`${STAFF.leadServices(lead.id)}/`, { services: ids }),
      // The conversion plan is derived from this, so it is stale the moment this
      // changes — and it is the thing that decides whether Convert is available.
      invalidates: [qk.all('leads'), qk.all('lead-conversion-plan')],
    },
    {
      onSuccess: () => {
        toast({ title: 'Saved', tone: 'success' });
        setEditing(false);
      },
      onError: (error) =>
        toast({
          title: 'That could not be saved',
          description:
            error instanceof ConflictError ? error.detail : 'Try again.',
          tone: 'warning',
        }),
    },
  );

  const converted = Boolean(lead.converted_client_id);

  return (
    <Card>
      <CardHeader
        title="What this would become"
        description={
          converted
            ? 'This enquiry has been converted. The matters belong to the client now.'
            : 'Choose when you qualify the enquiry. Conversion opens one matter for each.'
        }
      />
      <CardBody>
        {!editing && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              {chosen.length === 0 ? (
                // Said as a consequence, not as an empty state. "None" leaves somebody
                // wondering why the Convert button is unavailable two screens later.
                <p className="text-sm text-ink-muted">
                  Nothing chosen yet — this enquiry cannot be converted until it is.
                </p>
              ) : (
                <ul className="flex flex-col gap-0.5">
                  {(lead.services ?? []).map((service) => (
                    <li key={service.id} className="text-sm text-ink">
                      {service.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {!converted && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setDraft(chosen);
                  setEditing(true);
                }}
              >
                {chosen.length === 0 ? 'Choose' : 'Change'}
              </Button>
            )}
          </div>
        )}

        {editing && (
          <div className="flex flex-col gap-3">
            {available.length === 0 ? (
              <p className="text-sm text-ink-muted">
                This firm has no services configured yet. An administrator sets them up
                before an enquiry can be qualified.
              </p>
            ) : (
              available.map((service) => (
                <Checkbox
                  key={service.id}
                  label={service.name}
                  checked={draft.includes(service.id)}
                  onChange={(event) => {
                    // Read BEFORE the updater. React runs a functional setState
                    // callback after the handler has returned, and by then it has
                    // cleared `currentTarget` — so reading it in there throws
                    // "Cannot read properties of null (reading 'checked')" the
                    // first time anybody ticks a box.
                    const { checked } = event.currentTarget;
                    setDraft((current) =>
                      checked
                        ? [...current, service.id]
                        : current.filter((id) => id !== service.id),
                    );
                  }}
                />
              ))
            )}

            <div className="flex justify-end gap-2 border-t border-line pt-3">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={save.isPending}
                onClick={() => void save.mutateAsync(draft)}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
